/**
 * The Cortex Console Review blade (harness row CC2-11; spec.md 8A ViewReview,
 * requirements.md FR-8 to FR-11 and FR-16, design.md sections 6 and 11).
 *
 * What this component does: render the answer `GET /api/cortex/console/review` gave -
 * the phase columns in the order the answer names them, each package card under the
 * column the server gave it, the scope it applied, progress where an agent session
 * reports on a package, and a link from a package waiting on a decision to that
 * decision in the Decisions blade.
 *
 * What it deliberately does NOT do:
 *
 * - decide a column. Each card arrives with `phase`, the server's column for its stored
 *   phase, and is shown under that column and no other (requirements.md FR-9);
 * - invent a count. A package no agent session reports on shows no rows at all, never
 *   0 of 0 (FR-10);
 * - decide what a package waits on. The decision link is shown only where the answer
 *   names `blocking_decision_key`, and the `blocked_on` words otherwise (FR-11);
 * - show a disk path. The answer carries none, and nothing here reads a file.
 */
import React from "react";
import { ContentRegionLoader } from "../components/loading";
import { KanbanBoard, KanbanCard, KanbanColumn, kanbanStyles } from "../components/kanban";
import type {
  ConsoleAgentsScope,
  ConsoleRefusal,
  ConsoleReviewCard,
  ConsoleReviewResponse,
} from "../api/types";
import { idLabel } from "../labels/displayLabels";
import { rowsLine } from "./AgentsBlade";
import { reported } from "./OverviewBlade";
import styles from "./ReviewBlade.module.css";

/**
 * The words a person reads for each column the server names. The server owns the column
 * set (it sends `phases`); an id with no entry here is still shown, in words.
 */
const PHASE_LABELS: Record<string, string> = {
  building: "Building",
  waiting_on_person: "Waiting on a person",
  ready_to_integrate: "Ready to integrate",
  closed: "Closed",
};

export function phaseLabel(phase: string): string {
  return idLabel(phase, PHASE_LABELS);
}

/** The words a person reads for the gate verdict a `ready_to_integrate_signal` carries. */
const GATE_VERDICT_LABELS: Record<string, string> = {
  pass: "pass",
  fail: "fail",
  absent: "not yet available",
};

function gateVerdictLabel(verdict: string): string {
  return idLabel(verdict, GATE_VERDICT_LABELS);
}

export function isReview(
  body: ConsoleReviewResponse | ConsoleRefusal,
): body is ConsoleReviewResponse {
  return (body as ConsoleReviewResponse).response_type === "console_review";
}

export type ReviewBladeProps = {
  review: ConsoleReviewResponse | ConsoleRefusal | null;
  loading: boolean;
  /** The scope the person asked for. What was applied is on the answer itself. */
  requestedScope: ConsoleAgentsScope;
  onScopeChange: (scope: ConsoleAgentsScope) => void;
  onRefresh: () => void;
  /** Open the Decisions blade at this decision. */
  onOpenDecision: (decisionKey: string) => void;
};

function cardKey(card: ConsoleReviewCard): string {
  return card.feature_slug || card.package_rel || card.package_name_human || "package";
}

function ReviewCard({
  card,
  onOpenDecision,
}: {
  card: ConsoleReviewCard;
  onOpenDecision: (decisionKey: string) => void;
}) {
  const decisionKey = card.blocking_decision_key;
  return (
    <article data-testid={`review-card-${cardKey(card)}`}>
      <h4 className={styles.cardTitle}>{reported(card.package_name_human)}</h4>
      <p className={styles.line}>Stream: {reported(card.stream)}</p>
      {card.rows_passed === null || card.rows_passed === undefined ? (
        <p className={styles.line} data-testid="review-card-no-progress">
          No agent session reports progress on this package.
        </p>
      ) : (
        <p className={styles.line} data-testid="review-card-rows">
          {rowsLine(card)}
        </p>
      )}
      {card.first_unpassed_row ? (
        <p className={styles.line} data-testid="review-card-first-unpassed">
          First unpassed row: {card.first_unpassed_row}
        </p>
      ) : null}
      {card.last_heard_at ? (
        <p className={styles.line}>Last heard: {card.last_heard_at}</p>
      ) : null}
      {decisionKey ? (
        <button
          type="button"
          className={styles.decisionLink}
          data-testid={`review-card-decision-${decisionKey}`}
          onClick={() => onOpenDecision(decisionKey)}
        >
          Waiting on a decision ({decisionKey})
        </button>
      ) : card.blocked_on ? (
        <p
          className={`${styles.line} ${styles.blockedOn}`}
          data-testid="review-card-blocked-on"
        >
          Blocked on: {card.blocked_on}
        </p>
      ) : null}
      {card.note ? (
        <p className={styles.line} data-testid="review-card-note">
          Note: {card.note}
        </p>
      ) : null}
      <details className={styles.detail}>
        <summary className={styles.detailSummary}>Package state</summary>
        <p className={styles.line}>
          Stored phase: {card.current_phase ? idLabel(card.current_phase) : "not reported"}
        </p>
        <p className={styles.line}>
          Status: {card.current_status ? idLabel(card.current_status) : "not reported"}
        </p>
        <p className={styles.line}>Updated: {reported(card.updated_at)}</p>
        {card.closed_at ? <p className={styles.line}>Closed: {card.closed_at}</p> : null}
        {card.ready_to_integrate_signal ? (
          <p className={styles.line} data-testid="review-card-ready-signal">
            Branch currency:{" "}
            {card.ready_to_integrate_signal.behind_origin_tagd === null
              ? "not reported"
              : `${card.ready_to_integrate_signal.behind_origin_tagd} commits behind`}{" "}
            (threshold {card.ready_to_integrate_signal.behind_origin_threshold}). Gate
            verdict: {gateVerdictLabel(card.ready_to_integrate_signal.integration_gate_verdict)}.
          </p>
        ) : null}
        {card.ready_to_integrate_blocked_reason ? (
          <p
            className={`${styles.line} ${styles.blockedOn}`}
            data-testid="review-card-ready-blocked-reason"
          >
            Not yet ready: {card.ready_to_integrate_blocked_reason}
          </p>
        ) : null}
      </details>
    </article>
  );
}

export function ReviewBlade({
  review,
  loading,
  requestedScope,
  onScopeChange,
  onRefresh,
  onOpenDecision,
}: ReviewBladeProps) {
  const answered = review && isReview(review) ? review : null;
  const message = review?.message;
  const phases = answered?.phases;
  const cards = answered?.cards || [];

  return (
    <div className={styles.blade} data-testid="console-review-blade">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Review</h2>
          {answered?.scope_statement ? (
            <p className={styles.scopeStatement} data-testid="review-scope-statement">
              {answered.scope_statement}
            </p>
          ) : null}
        </div>
        <div className={styles.controls}>
          <label className={styles.switch}>
            <input
              type="checkbox"
              data-testid="review-all-streams-switch"
              checked={requestedScope === "all_streams"}
              onChange={(event) =>
                onScopeChange(event.target.checked ? "all_streams" : "granted_streams")
              }
            />
            All streams
          </label>
          <button
            type="button"
            className={styles.refresh}
            data-testid="review-refresh"
            onClick={onRefresh}
          >
            Refresh
          </button>
        </div>
      </div>

      {answered?.board_purpose_statement ? (
        <p className={styles.boardPurpose} data-testid="review-board-purpose">
          {answered.board_purpose_statement}
        </p>
      ) : null}

      {message ? (
        <p className={styles.message} data-testid="review-message">
          {message}
        </p>
      ) : null}

      {loading ? (
        <ContentRegionLoader label="Loading the Review board" data-testid="review-loading" />
      ) : null}

      {!loading && phases ? (
        <KanbanBoard columnsTestId="review-columns">
          {phases.map((phase) => {
            const inColumn = cards.filter((card) => card.phase === phase);
            return (
              <section
                key={phase}
                className={kanbanStyles.column}
                data-testid={`review-column-${phase}`}
              >
                <KanbanColumn
                  title={phaseLabel(phase)}
                  count={inColumn.length}
                  emptyLabel="No packages"
                >
                  {inColumn.map((card) => (
                    <KanbanCard key={cardKey(card)} board="review" status={phase}>
                      <ReviewCard card={card} onOpenDecision={onOpenDecision} />
                    </KanbanCard>
                  ))}
                </KanbanColumn>
              </section>
            );
          })}
        </KanbanBoard>
      ) : null}
    </div>
  );
}
